import { useGetProxied } from '~/backend/connector-api/connector-components';
import { APIType } from '~/backend/types';
import { useIsInSessionRoute } from '../../session/useIsInSessionRoute';
import { useLabelingBackend } from './useBackend';

/**
 * A custom hook that retrieves labeling data,
 * deciding between proxied data or backend data based on the URL path.
 *
 * @param {APIType} apiType - The backend API type required (contouring, data, workspace, etc.)
 */
export const useLabelingData = (apiType: APIType) => {
    const isInConnectorRoute = useIsInSessionRoute();

    // If we're NOT in the connector route, this fetches labeling data from the backend
    // 'enabled' is set to the opposite of isInConnectorRoute
    const { data: backendLabelingData, isLoading: isLoadingBackend } =
        useLabelingBackend(apiType, !isInConnectorRoute);

    // If we ARE in the connector route, this fetches labeling data via the connector's proxied route
    const { data: proxiedLabelingData, isLoading: isLoadingProxied } =
        useGetProxied(
            {
                pathParams: {
                    rest:
                        apiType === APIType.WORKSPACE
                            ? 'labeling'
                            : `${apiType}/labeling`,
                },
            },
            {
                enabled: isInConnectorRoute,
            }
        );

    // Return the appropriate data (proxied vs. backend) and its loading state,
    // depending on which route we are in
    return {
        data: isInConnectorRoute ? proxiedLabelingData : backendLabelingData,
        isLoading: isInConnectorRoute ? isLoadingProxied : isLoadingBackend,
    };
};
